import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";

import ReactGA from 'react-ga4';

import ReportABug from './components/Home/ReportABug';
import HomePage from './components/Home/HomePage';
import Navbar from './components/Home/Navbar/Navbar';
import ErrorDialog from './components/Auth/ErrorDialog';

import "bootstrap/dist/css/bootstrap.min.css";
import QuestionnaireSection from './components/Questionnaire/QuestionnaireSection';
import NPIForm from './components/Auth/NPIForm.js';
import { setLoggedIn, setLoggingOut } from './redux/slices/auth/authSlice.js';
import { useCheckCognitoIdMutation } from './redux/slices/auth/authThunk.js';
import { getInsuranceOptions, getSpecialtyOptions } from './redux/slices/shared/sharedThunk.js';
import { useDispatch, useSelector } from 'react-redux';
import WholeScreenSkeleton from './components/Misc/WholeScreenSkeleton copy.js';
import { setPersonInfo, setPhysicianId } from './redux/slices/physician/physicianSlice.js';
import { useGetPhysicianPreferredInsuranceQuery, useGetPhysicianLocationQuery, useGetGroupsQuery, useUpdateLastSeenMutation } from './redux/slices/physician/physicianThunk.js';
import { loginPhysician } from './redux/slices/auth/authThunk.js';
import store from './redux/store.js';
import { Snackbar } from '@mui/material';
import { setSnackbarOpen } from './redux/slices/shared/sharedSlice.js';
import PersonInfoForm from './components/Home/PersonInfoForm/PersonInfoForm.js';
import { BASE_URL } from './redux/util/constants.js';

const AppContainer = () => {
    const dispatch = useDispatch();
    const { physicianId } = useSelector(state => state.physician);
    const { authLoading } = useSelector(state => state.auth);
    const { snackbarOpen, snackbarMessage } = useSelector(state => state.shared);

    const [ checkCognitoId, { isLoading: cognitoIdLoading }] = useCheckCognitoIdMutation();
    const [ updateLastSeen, { isLoading: updateLastSeenLoading } ] = useUpdateLastSeenMutation();

    const skipCondition = { skip: !physicianId };
    const { isLoading: insurancePreferenceLoading } = useGetPhysicianPreferredInsuranceQuery(skipCondition);
    const { isLoading: physicianLocationLoading } = useGetPhysicianLocationQuery(skipCondition);
    const { isLoading: groupsLoading, refetch: refetchGroups, error: fetchingDocsError } = useGetGroupsQuery({skip: true});

    const [loadingMetadata, setLoadingMetadata] = useState(false);

    const isValidDoctor = async () => {
        try {
            const refetchedGroups = await refetchGroups({ forceRefetch: true }).unwrap();

            const currentUrl = window.location.href;
            const onHomePage = currentUrl.includes("/home");
            const onNpiPage = currentUrl.includes("/npi");
            const isProvider = refetchedGroups?.includes("providers");
    
            if (onHomePage && isProvider) return;
            if (onNpiPage && isProvider) {
                window.location.href = `${BASE_URL}/home`;
                return;
            }
            if (onNpiPage && !isProvider) return;
    
            // Default redirection if not on NPI or Home page
            window.location.href = `${BASE_URL}/npi`;
    
        } catch (error) {
            console.error("Error in isValidDoctor:", error);
            console.log(fetchingDocsError);
        }
    };

    const handleLoggingIn = async () => {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        const accessToken = params.get("access_token");

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", params.get("refresh_token"));
    
        try {
            await handleCheckCognitoId(accessToken, physicianId);
        } catch (error) {
            console.error(error);
        }

        await isValidDoctor();
    }

    const handleLoggedIn = async () => {
        try {
            // Check if current user link is in local storage
            const currentUserLink = localStorage.getItem("current_user");
            if (currentUserLink) {
                // Redirect and clean up
                window.location.href = currentUserLink;
                localStorage.removeItem("current_user");
                return;
            }
            await handleCheckCognitoId(localStorage.getItem("access_token"), physicianId);
            
            dispatch(setLoggedIn(true));
            dispatch(setLoggingOut(false));
            dispatch(getInsuranceOptions());
            dispatch(getSpecialtyOptions());
            console.log("test2")
            await updateLastSeen({ physicianId }).unwrap();
            await isValidDoctor();
        } catch (error) {
            console.error("Error in authFlowLoggedIn:", error);
        } 
    }

    const handleCheckCognitoId = async (accessToken) => {
        const res = await checkCognitoId({ accessToken, sessionId: physicianId }).unwrap();
        console.log(res)
        if (res?.user?.id) {
            dispatch(setLoggedIn(true));
            dispatch(setPersonInfo(res.user));
            dispatch(setPhysicianId(res.user.id));
            window.history.replaceState(null, null, window.location.pathname);
        } else {
            console.log("Error getting cognito id.");
            console.log(res);
        }
    }

    useEffect(() => {
        const setMetadata = async () => {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            const authenticated = params.get("authentication_status");
            const accessToken = localStorage.getItem("access_token");
            const refreshToken = localStorage.getItem("refresh_token");

            if (authenticated) {
                await handleLoggingIn();
            } else if (accessToken && accessToken !== "null" && refreshToken && refreshToken !== "null") {
                await handleLoggedIn();
            } else {
                dispatch(loginPhysician());
            }
            console.log(store.getState());

            ReactGA.send({ hitType: "pageview", page: window.location, title: "Home Page View" });
        }
        setLoadingMetadata(true);
        setMetadata(); 
        setLoadingMetadata(false);
    }, [dispatch]);

    useEffect(() => {}, [])

    const measurementId = "G-90K8S7490Z"; 
    ReactGA.initialize(measurementId);

    return (
        <>
            <BrowserRouter>
                <Navbar />
                {authLoading || 
                insurancePreferenceLoading || 
                physicianLocationLoading || 
                cognitoIdLoading || 
                loadingMetadata || 
                groupsLoading || 
                updateLastSeenLoading || 
                !physicianId ? 
                    <WholeScreenSkeleton /> : null
                }
                <Routes>
                    <Route element={<ErrorDialog />}>
                        <Route path="/" element={<Navigate to="/home" />} />
                        <Route path="/home" element={<HomePage />} />
                        <Route path="/home/*" element={<HomePage />} />
                        <Route path="/profile" element={<PersonInfoForm />} />
                        <Route path="/npi" element={<NPIForm />} />
                        <Route path="/onboard/questionnaire/:index" element={<QuestionnaireSection />} />
                        <Route path="*" element={<Navigate to="/home" />} />
                    </Route>
                </Routes>
                <ReportABug />
            </BrowserRouter>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={snackbarOpen}
                onClose={() => dispatch(setSnackbarOpen(false))}
                message={snackbarMessage}
                key={'bottomright'}
                autoHideDuration={2000}
                sx={{ position: "inline-block", zIndex: 9999999}}
            />    
        </>
        
    );
};

export default AppContainer;